import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useRef, useState } from "react";
import { recaptchaSiteKey } from "env";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import RecaptchaField, { RecaptchaFieldRef } from "components/RecaptchaField";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import { startPasswordlessLogin, isRecaptchaRequiredError, isServiceUnavailableError } from "services/auth.service";
import { useLoginPage } from "hooks/useLoginPage";
import { useClearStorageOnQueryParam } from "hooks/useClearStorageOnQueryParam";
import { isValidEmail } from "utils/validators";
import TextInput from "components/TextInput";
import { useText } from "context/TextProvider";
import { setFlowState } from "../../storage";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsFlow, AnalyticsMethod, AnalyticsStage, AnalyticsStatus } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";

const Login = (): JSX.Element => {
  const text = useText();
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  useClearStorageOnQueryParam();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_LOGIN_SCREEN);
  const [email, setEmail] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef<RecaptchaFieldRef>(null);
  const enableRecaptcha = loginPageConfig?.enableRecaptcha ?? false;

  const validateForm = (): boolean => {
    if (!email) {
      setErrorMessage(text.login.emailRequired);
      return false;
    }
    if (!isValidEmail(email)) {
      setErrorMessage(text.login.invalidEmail);
      return false;
    }
    if (enableRecaptcha && !recaptchaToken) {
      setErrorMessage(text.login.recaptchaRequired);
      return false;
    }
    setErrorMessage("");
    return true;
  };

  const handleLogin = async (): Promise<void> => {
    if (!validateForm()) return;
    setLoading(true);
    setErrorMessage("");
    trackEvent(AnalyticsEvent.PASSWORDLESS_LOGIN_STARTED, { method: AnalyticsMethod.PASSWORDLESS, flow: AnalyticsFlow.STANDARD, status: AnalyticsStatus.STARTED });
    setFlowState(configId, { loginStartedAt: Date.now() });
    try {
      const codes = await startPasswordlessLogin({
        loginId: email,
        recaptchaToken,
        enableRecaptcha: enableRecaptcha,
        url: loginPageConfig?.urls?.start_passwordless_login,
      });
      setFlowState(configId, { route: "otp-verification", email, code: codes.code });
      appNavigate(`/otp-verification`);
    } catch (err) {
      console.error("Login error:", err);
      setErrorMessage(
        isRecaptchaRequiredError(err)
          ? text.login.recaptchaRequired
          : isServiceUnavailableError(err)
          ? text.common.serviceUnavailable
          : text.login.genericError
      );
      trackEvent(AnalyticsEvent.PASSWORDLESS_LOGIN_FAILED, {
        method: AnalyticsMethod.PASSWORDLESS,
        flow: AnalyticsFlow.STANDARD,
        status: AnalyticsStatus.FAILED,
        stage: AnalyticsStage.START,
      });
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
      setRecaptchaToken("");
    }
  };

  return (
    <>
      <PageMeta name="Login" />
      <PageTitle>{text.login.title}</PageTitle>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <TextInput
        type="email"
        value={email}
        placeholder={text.login.emailPlaceholder}
        onChange={(e) => setEmail(e.target.value)}
        disabled={loading}
      />

      {enableRecaptcha && (
        <RecaptchaField
          ref={recaptchaRef}
          sitekey={recaptchaSiteKey}
          onChange={(token) => setRecaptchaToken(token ?? "")}
          onError={(message) => setErrorMessage(message)}
          theme="dark"
          size="normal"
          tabIndex={0}
        />
      )}

      <LoadingButton
        loading={loading}
        onClick={handleLogin}
        disabled={loading}
        sx={{ width: "100%", height: "3.5rem", mb: "1rem" }}
      >
        {text.login.submitButton}
      </LoadingButton>
    </>
  );
};

export default React.memo(Login);
